import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState, type RefObject } from 'react';

import {
  createFullHeadMPRSession,
  type FullHeadMPRSession,
  type FullHeadMPRSavedView,
  type FullHeadMPRProjection,
  type FullHeadMPRViewSnapshot,
  type FullHeadMPRActivePane,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector } from '@/shared/hooks';
import { debug } from '@/shared/lib';

import { reportsModel } from '@/entities/reports';

import {
  readFullHeadMprPaneLayout,
  type FullHeadMprOriginalSlice,
} from '@/features/MPR';

import {
  FULL_HEAD_MPR_ERROR_CODES,
  FULL_HEAD_MPR_SESSION_PROJECTIONS,
  type FullHeadMPRErrorCode,
  type FullHeadMprSliceGroupView,
  type FullHeadMprSliceRecordView,
  type FullHeadMprSliceTileView,
  type ToothSliceGeometry,
  type ToothSliceOptionsBySeries,
} from '../config';
import { loadFullHeadMprOriginalSlice } from '../lib';

export const useFullHeadMprSliceSession = (params: {
  containerRef: RefObject<HTMLDivElement>;
  group: FullHeadMprSliceGroupView | null;
  input: ToothSliceGeometry | null;
  record: FullHeadMprSliceRecordView | undefined;
  sliceOptions: ToothSliceOptionsBySeries;
  tile: FullHeadMprSliceTileView;
  toothID: string;
  groups?: FullHeadMprSliceGroupView[];
  visibleProjections?: readonly FullHeadMPRProjection[];
}) => {
  const {
    containerRef,
    group,
    groups,
    sliceOptions,
    toothID,
    tile,
    visibleProjections = FULL_HEAD_MPR_SESSION_PROJECTIONS,
  } = params;
  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const loading = useAppSelector(
    reportsModel.selectors.selectFullHeadMprLoading(report?.ID ?? ''),
  );
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const sessionRef = useRef<FullHeadMPRSession | null>(null);
  const initialViewRef = useRef<FullHeadMPRSavedView | null>(null);
  const [session, setSession] = useState<FullHeadMPRSession | null>(null);
  const [original, setOriginal] = useState<FullHeadMprOriginalSlice | null>(
    null,
  );
  const [snapshots, setSnapshots] = useState<
    Partial<Record<FullHeadMPRProjection, FullHeadMPRViewSnapshot>>
  >({});
  const [activePane, setActivePane] = useState<FullHeadMPRActivePane | null>(
    null,
  );
  const [isActiveViewReady, setIsActiveViewReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<FullHeadMPRErrorCode | null>(
    null,
  );
  const [retryCount, setRetryCount] = useState(0);
  const contextKey = `${toothID}:${tile.id}:${retryCount}`;
  const [readyContextKey, setReadyContextKey] = useState<string | null>(null);
  const thicknessMm =
    tile.kind === 'series'
      ? {
          axial: sliceOptions.axial.thicknessMm,
          coronal: sliceOptions.frontal.thicknessMm,
          sagittal: sliceOptions.sagittal.thicknessMm,
        }
      : {};
  const thicknessRef = useRef(thicknessMm);
  thicknessRef.current = thicknessMm;
  const seriesGroups = groups ?? (group ? [group] : []);
  const stepForSeries = (seriesName: FullHeadMprSliceGroupView['seriesName']) =>
    seriesGroups.find((series) => series.seriesName === seriesName)
      ?.sliceStepMm ??
    (tile.kind === 'series' ? sliceOptions[seriesName].sliceStepMm : undefined);
  const stepsMm = {
    axial: stepForSeries('axial'),
    coronal: stepForSeries('frontal'),
    sagittal: stepForSeries('sagittal'),
  };
  const stepsRef = useRef(stepsMm);
  stepsRef.current = stepsMm;
  const visibleRef = useRef(visibleProjections);
  visibleRef.current = visibleProjections;

  useEffect(() => {
    session?.setSliceThicknessMm(thicknessRef.current);
  }, [
    session,
    sliceOptions.axial.thicknessMm,
    sliceOptions.frontal.thicknessMm,
    sliceOptions.sagittal.thicknessMm,
    tile.kind,
  ]);

  useEffect(() => {
    session?.setSliceStepMm(stepsRef.current);
  }, [session, stepsMm.axial, stepsMm.coronal, stepsMm.sagittal]);

  useEffect(() => {
    session?.setVisibleProjections(visibleProjections);
  }, [session, visibleProjections]);

  useEffect(() => {
    setReadyContextKey(null);
    const container = containerRef.current;
    if (!container || loading !== 'succeeded') {
      return;
    }
    const abort = new AbortController();
    const presentation = { id: crypto.randomUUID(), isReady: false };
    const scheduled = { frame: 0 };
    const pendingProjections = new Set<FullHeadMPRProjection>();
    const update = (projection?: FullHeadMPRProjection) => {
      if (abort.signal.aborted || !presentation.isReady) {
        return;
      }
      (projection ? [projection] : FULL_HEAD_MPR_SESSION_PROJECTIONS).forEach(
        (pane) => pendingProjections.add(pane),
      );
      if (scheduled.frame) {
        return;
      }
      scheduled.frame = requestAnimationFrame(() => {
        scheduled.frame = 0;
        const current = sessionRef.current;
        if (!current || abort.signal.aborted) {
          return;
        }
        const updates = [...pendingProjections].map((pane) => ({
          projection: pane,
          snapshot: current.getViewSnapshot(pane),
        }));
        pendingProjections.clear();
        setSnapshots((previous) =>
          updates.reduce((next, { projection, snapshot }) => {
            if (isEqual(next[projection] ?? null, snapshot)) {
              return next;
            }
            if (!snapshot) {
              const remaining = { ...next };
              delete remaining[projection];
              return remaining;
            }
            return { ...next, [projection]: snapshot };
          }, previous),
        );
        const nextActivePane = current.getActivePane();
        setActivePane((previous) =>
          previous?.element === nextActivePane?.element &&
          previous?.projection === nextActivePane?.projection
            ? previous
            : nextActivePane,
        );
        setIsActiveViewReady(current.isViewReady());
        setReadyContextKey(contextKey);
      });
    };
    setErrorMessage(null);
    setSnapshots({});
    setSession(null);
    setIsActiveViewReady(false);
    setOriginal(null);
    initialViewRef.current = null;
    const initialize = async () => {
      const { input, group, record, sliceOptions, tile } = paramsRef.current;
      const original = await loadFullHeadMprOriginalSlice({
        group,
        input,
        record,
        signal: abort.signal,
        sliceOptions,
        tile,
        toothID,
      });
      abort.signal.throwIfAborted();
      const initial = {
        original,
        display:
          record?.editorState?.displayView ?? tile.displayView ?? original.view,
      };
      initialViewRef.current = initial.display;
      setOriginal(initial.original);
      const created = createFullHeadMPRSession(container);
      created.setPaneLayout(readFullHeadMprPaneLayout());
      created.setPrimaryProjection(initial.display.projection);
      created.setSliceThicknessMm(thicknessRef.current);
      created.setSliceStepMm(stepsRef.current);
      created.setVisibleProjections(visibleRef.current);
      sessionRef.current = created;
      setSession(created);
      created.addEventListener('fullHeadMPRPresentationReady', (event) => {
        if (
          !abort.signal.aborted &&
          event.presentationID === presentation.id &&
          event.volumeFileID === initial.display.volumeFileID
        ) {
          presentation.isReady = true;
          update();
        }
      });
      created.addEventListener('fullHeadMPRViewUpdated', ({ projection }) =>
        update(projection),
      );
      created.addEventListener(
        'fullHeadMPRActivePaneChanged',
        ({ projection }) => update(projection),
      );
      created.addEventListener('fullHeadMPRFocusChanged', () => update());
      created.addEventListener('fullHeadMPRError', ({ message }) => {
        if (!abort.signal.aborted) {
          debug(`[FullHeadMPR] event=slice_session_failed message=${message}`);
          setErrorMessage(FULL_HEAD_MPR_ERROR_CODES.startViewer);
        }
      });
      await created.start(initial.display, { presentationID: presentation.id });
      update();
    };
    void initialize().catch((error: unknown) => {
      if (!abort.signal.aborted) {
        debug(
          `[FullHeadMPR] event=slice_session_failed message=${error instanceof Error ? error.message : String(error)}`,
        );
        setErrorMessage(FULL_HEAD_MPR_ERROR_CODES.startViewer);
      }
    });
    return () => {
      sessionRef.current?.dispose();
      abort.abort();
      cancelAnimationFrame(scheduled.frame);
      sessionRef.current = null;
    };
  }, [contextKey, toothID, containerRef, loading]);

  return {
    activePane,
    errorMessage:
      loading === 'failed'
        ? FULL_HEAD_MPR_ERROR_CODES.dataStream
        : errorMessage,
    isReady:
      loading === 'succeeded' &&
      readyContextKey === contextKey &&
      !errorMessage &&
      visibleProjections.every((projection) => snapshots[projection]),
    isActiveViewReady,
    original,
    session,
    snapshots,
    getSnapshot: (projection?: FullHeadMPRProjection) =>
      sessionRef.current?.isViewReady(projection)
        ? sessionRef.current.getViewSnapshot(projection)
        : null,
    reset: () => {
      if (initialViewRef.current) {
        sessionRef.current?.restoreSavedView(initialViewRef.current);
      }
    },
    retry: () => setRetryCount((count) => count + 1),
  };
};
